// BannerEditor — dash-329 · Track B (passo 1)
//
// Compõe um criativo promocional para redes sociais a partir dos dados da peça,
// usando o Fabric.js v6 já em produção (mesma stack do editor de etiquetas).
//
// Passo 1 (este ficheiro): fundo PRESET + preço de campanha MANUAL + export PNG.
// Passos seguintes (não aqui): fonte "campanha ativa" (endpoint
// GET /v2/parts/:uuid/active-campaigns — handoff tpsoft-campanhas-desconto) e
// troca do fundo preset por FUNDO-IA (Track A greenparts / tpsoft-ai-flows).
//
// A foto é carregada com crossOrigin='anonymous'. Se o CDN das imagens não
// enviar cabeçalhos CORS, o canvas fica "tainted" e a exportação falha — nesse
// caso avisamos o utilizador em vez de rebentar (ver handleExport).

import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as fabric from "fabric";

const TP_LOGO_FALLBACK =
  "https://printer.tp.software/images/logo-tp-software.png";

// Working canvas width (px). Export usa multiplier = alvo/WORK para sair em
// resolução total (a partir das imagens-fonte), com interação de rato correta.
const WORK_W = 500;

const FORMATS = [
  { key: "square", name: "Quadrado", label: "1080×1080", w: 1080, h: 1080 },
  { key: "portrait", name: "Vertical", label: "1080×1350", w: 1080, h: 1350 },
  { key: "landscape", name: "Horizontal", label: "1200×630", w: 1200, h: 630 },
];

// Presets de fundo. `fg` = cor do texto; `accent` = cor do preço de campanha.
const BG_PRESETS = [
  { key: "white", name: "Branco", type: "solid", fill: "#ffffff", fg: "#111827", accent: "#81BB42" },
  { key: "dark", name: "Escuro", type: "solid", fill: "#111827", fg: "#ffffff", accent: "#81BB42" },
  { key: "green", name: "Verde TP", type: "gradient", from: "#81BB42", to: "#4d7c2a", fg: "#ffffff", accent: "#ffffff" },
  { key: "blue", name: "Azul", type: "gradient", from: "#2563EB", to: "#1e3a8a", fg: "#ffffff", accent: "#fbbf24" },
  { key: "warm", name: "Quente", type: "gradient", from: "#f59e0b", to: "#b91c1c", fg: "#ffffff", accent: "#ffffff" },
];

const fmtPrice = (v) => {
  const n = Number(v);
  if (!isFinite(n) || v === "" || v == null) return "";
  return `${n.toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
};

const loadImage = (url) =>
  fabric.FabricImage.fromURL(url, { crossOrigin: "anonymous" }).catch(() => null);

const BannerEditor = ({ part, companyLogoUrl }) => {
  const images = Array.isArray(part?.image_list) ? part.image_list : [];

  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);

  const [formatKey, setFormatKey] = useState("square");
  const [presetKey, setPresetKey] = useState("white");
  const [imageUrl, setImageUrl] = useState(images[0]?.image_url || "");
  const [headline, setHeadline] = useState(part?.namePartPT || "");
  const [normalPrice, setNormalPrice] = useState(
    part?.part_price != null ? String(part.part_price) : ""
  );
  const [campaignPrice, setCampaignPrice] = useState("");
  const [exporting, setExporting] = useState(false);

  const format = FORMATS.find((f) => f.key === formatKey) || FORMATS[0];
  const dispW = WORK_W;
  const dispH = Math.round(WORK_W * (format.h / format.w));

  // (Re)cria o canvas Fabric sempre que as dimensões mudam (formato).
  useEffect(() => {
    if (!canvasElRef.current) return undefined;
    const c = new fabric.Canvas(canvasElRef.current, {
      width: dispW,
      height: dispH,
      preserveObjectStacking: true,
      selection: true,
    });
    fabricRef.current = c;
    return () => {
      c.dispose();
      fabricRef.current = null;
    };
  }, [dispW, dispH]);

  // (Re)desenha a cena quando muda qualquer input.
  useEffect(() => {
    let cancelled = false;
    const c = fabricRef.current;
    if (!c) return undefined;

    const preset =
      BG_PRESETS.find((p) => p.key === presetKey) || BG_PRESETS[0];

    (async () => {
      // Carrega imagens em paralelo (browser faz cache após a 1.ª vez).
      const [photo, logo] = await Promise.all([
        imageUrl ? loadImage(imageUrl) : Promise.resolve(null),
        loadImage(companyLogoUrl || TP_LOGO_FALLBACK),
      ]);
      if (cancelled || fabricRef.current !== c) return;

      c.remove(...c.getObjects());

      // ── Fundo ────────────────────────────────────────────────────────
      const bgFill =
        preset.type === "gradient"
          ? new fabric.Gradient({
              type: "linear",
              coords: { x1: 0, y1: 0, x2: 0, y2: dispH },
              colorStops: [
                { offset: 0, color: preset.from },
                { offset: 1, color: preset.to },
              ],
            })
          : preset.fill;
      c.add(
        new fabric.Rect({
          left: 0,
          top: 0,
          width: dispW,
          height: dispH,
          fill: bgFill,
          selectable: false,
          evented: false,
        })
      );

      // ── Foto da peça (encaixe preservando rácio) ─────────────────────
      if (photo) {
        const boxW = dispW * 0.66;
        const boxH = dispH * 0.5;
        const scale = Math.min(boxW / photo.width, boxH / photo.height);
        photo.scale(scale);
        photo.set({
          left: (dispW - photo.width * scale) / 2,
          top: dispH * 0.18,
        });
        c.add(photo);
      }

      // ── Logo (topo-esquerda) ─────────────────────────────────────────
      if (logo) {
        const lw = dispW * 0.24;
        logo.scale(lw / logo.width);
        logo.set({ left: dispW * 0.06, top: dispH * 0.05 });
        c.add(logo);
      }

      // ── Headline (nome da peça) ──────────────────────────────────────
      if (headline) {
        c.add(
          new fabric.Textbox(headline, {
            left: dispW * 0.06,
            top: dispH * 0.7,
            width: dispW * 0.88,
            fontSize: Math.round(dispW * 0.05),
            fontFamily: "Arial",
            fontWeight: "bold",
            fill: preset.fg,
            textAlign: "left",
          })
        );
      }

      // ── Bloco de preços ──────────────────────────────────────────────
      const hasCampaign = String(campaignPrice).trim() !== "";
      const normalTxt = fmtPrice(normalPrice);
      const campTxt = fmtPrice(campaignPrice);
      const priceTop = dispH * 0.82;

      if (hasCampaign && campTxt) {
        if (normalTxt) {
          c.add(
            new fabric.Textbox(normalTxt, {
              left: dispW * 0.06,
              top: priceTop,
              fontSize: Math.round(dispW * 0.05),
              fontFamily: "Arial",
              fill: preset.fg,
              opacity: 0.7,
              linethrough: true,
            })
          );
        }
        c.add(
          new fabric.Textbox(campTxt, {
            left: dispW * 0.06,
            top: priceTop + dispH * 0.06,
            fontSize: Math.round(dispW * 0.11),
            fontFamily: "Arial",
            fontWeight: "bold",
            fill: preset.accent,
          })
        );
      } else if (normalTxt) {
        c.add(
          new fabric.Textbox(normalTxt, {
            left: dispW * 0.06,
            top: priceTop,
            fontSize: Math.round(dispW * 0.11),
            fontFamily: "Arial",
            fontWeight: "bold",
            fill: preset.fg,
          })
        );
      }

      c.requestRenderAll();
    })();

    return () => {
      cancelled = true;
    };
  }, [
    imageUrl,
    companyLogoUrl,
    presetKey,
    headline,
    normalPrice,
    campaignPrice,
    dispW,
    dispH,
  ]);

  const handleExport = () => {
    const c = fabricRef.current;
    if (!c) return;
    setExporting(true);
    try {
      c.discardActiveObject();
      c.requestRenderAll();
      const dataUrl = c.toDataURL({
        format: "png",
        multiplier: format.w / dispW,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      const idPart = part?.parts_internal_id || part?.uuid || "peca";
      a.download = `banner-${idPart}-${format.key}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Banner exportado (PNG)");
    } catch (err) {
      // SecurityError = canvas "tainted" (imagem sem CORS). Não expõe stack.
      toast.error(
        "Não foi possível exportar: a imagem do produto não permite CORS. " +
          "É necessário servir as fotos com cabeçalhos CORS (a validar no deploy)."
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="tp-banner-body">
      <div className="tp-banner-stage">
        <div className="tp-banner-canvas-wrap">
          <canvas ref={canvasElRef} />
        </div>
        <button
          type="button"
          className="tp-btn-primary"
          onClick={handleExport}
          disabled={exporting}
        >
          <i className="bi bi-download" /> Exportar PNG ({format.label})
        </button>
      </div>

      <div className="tp-banner-controls">
        <div className="tp-banner-group">
          <label>Formato</label>
          <div className="tp-banner-formats">
            {FORMATS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`tp-banner-chip${
                  f.key === formatKey ? " is-active" : ""
                }`}
                onClick={() => setFormatKey(f.key)}
                title={f.label}
              >
                {f.name}
              </button>
            ))}
          </div>
        </div>

        <div className="tp-banner-group">
          <label>Fundo</label>
          <div className="tp-banner-presets">
            {BG_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`tp-banner-swatch${
                  p.key === presetKey ? " is-active" : ""
                }`}
                onClick={() => setPresetKey(p.key)}
                title={p.name}
                style={{
                  background:
                    p.type === "gradient"
                      ? `linear-gradient(135deg, ${p.from}, ${p.to})`
                      : p.fill,
                }}
              />
            ))}
          </div>
          <div className="tp-banner-hint">
            Fundo por IA (foto sem fundo) chega numa fase seguinte.
          </div>
        </div>

        {images.length > 1 && (
          <div className="tp-banner-group tp-banner-field">
            <label>Foto</label>
            <select
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            >
              {images.map((img, i) => (
                <option key={img.image_url || i} value={img.image_url}>
                  Foto {i + 1}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="tp-banner-group tp-banner-field">
          <label>Título</label>
          <input
            type="text"
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="Nome da peça"
          />
        </div>

        <div className="tp-banner-group tp-banner-field">
          <label>Preço normal</label>
          <input
            type="number"
            step="0.01"
            value={normalPrice}
            onChange={(e) => setNormalPrice(e.target.value)}
            placeholder="0.00"
          />
        </div>

        <div className="tp-banner-group tp-banner-field">
          <label>Preço de campanha</label>
          <input
            type="number"
            step="0.01"
            value={campaignPrice}
            onChange={(e) => setCampaignPrice(e.target.value)}
            placeholder="(opcional)"
          />
          <div className="tp-banner-hint">
            Manual por agora; seleção de campanha ativa da peça numa fase
            seguinte.
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerEditor;
